import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ActionDependant,
    evaluateTemplateValue,
    GlobalDataContext,
    TemplateContext,
    useEvaluatedAttributes,
} from '@ea-lab/reactive-json';
import parse, { domToReact } from 'html-react-parser';
import { common, createLowlight } from 'lowlight';
import { toHtml } from 'hast-util-to-html';
import { AlertOctagon, AlertTriangle, Calendar, Check, ChevronRight, CircleCheck, Copy, Info, Lightbulb, ListChecks, ShieldAlert, StickyNote } from 'lucide-react';

const lowlight = createLowlight(common);

const CALLOUT_CONFIG = {
    info:         { icon: <Info size={16} />,           label: 'Info',         classes: 'bg-blue-50 border-blue-200 text-blue-800' },
    tip:          { icon: <Lightbulb size={16} />,      label: 'Astuce',       classes: 'bg-teal-50 border-teal-200 text-teal-800' },
    warning:      { icon: <AlertTriangle size={16} />,  label: 'Attention',    classes: 'bg-amber-50 border-amber-200 text-amber-800' },
    caution:      { icon: <ShieldAlert size={16} />,    label: 'Précaution',   classes: 'bg-orange-50 border-orange-200 text-orange-800' },
    danger:       { icon: <AlertOctagon size={16} />,   label: 'Danger',       classes: 'bg-red-50 border-red-200 text-red-800' },
    success:      { icon: <CircleCheck size={16} />,    label: 'Succès',       classes: 'bg-emerald-50 border-emerald-200 text-emerald-800' },
    note:         { icon: <StickyNote size={16} />,     label: 'Note',         classes: 'bg-stone-100 border-stone-300 text-stone-700' },
    prerequisite: { icon: <ListChecks size={16} />,     label: 'Prérequis',    classes: 'bg-iris-50 border-iris-200 text-iris-800' },
};

function formatInlineDate(dateStr) {
    if (!dateStr) return '';
    try {
        const d = new Date(dateStr + 'T00:00:00');
        if (isNaN(d.getTime())) return dateStr;
        return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(d);
    } catch {
        return dateStr;
    }
}

const BADGE_COLOR_MAP = {
    default: 'bg-stone-100 text-stone-700 border-stone-200',
    gray:    'bg-stone-100 text-stone-700 border-stone-200',
    brown:   'bg-amber-50 text-amber-800 border-amber-200',
    orange:  'bg-orange-50 text-orange-800 border-orange-200',
    yellow:  'bg-yellow-50 text-yellow-800 border-yellow-200',
    green:   'bg-emerald-50 text-emerald-800 border-emerald-200',
    blue:    'bg-blue-50 text-blue-800 border-blue-200',
    purple:  'bg-iris-50 text-iris-800 border-iris-200',
    pink:    'bg-pink-50 text-pink-800 border-pink-200',
    red:     'bg-red-50 text-red-800 border-red-200',
};

function slugify(text) {
    return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        || 'heading';
}

function getTextContent(node) {
    if (!node) return '';
    if (node.type === 'text') return node.data || '';
    if (node.children) return node.children.map(getTextContent).join('');
    return '';
}

function CopyButton({ text }) {
    const [copied, setCopied] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => () => clearTimeout(timerRef.current), []);

    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => setCopied(false), 2000);
        }).catch(() => {});
    }, [text]);

    return (
        <button type="button" onClick={handleCopy} className="doc-code-copy" aria-label="Copier le code">
            {copied
                ? <><Check size={12} /> Copié</>
                : <><Copy size={12} /> Copier</>
            }
        </button>
    );
}

function highlightCode(code, language) {
    if (!language) return null;
    try {
        const tree = lowlight.highlight(language, code);
        return toHtml(tree);
    } catch {
        return null;
    }
}

const ALLOWED_TAGS = new Set([
    'a', 'abbr', 'b', 'blockquote', 'br', 'caption', 'code', 'col', 'colgroup',
    'details', 'div', 'em', 'figure', 'figcaption', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'hr', 'i', 'img', 'input', 'label', 'li', 'mark', 'ol', 'p', 'pre',
    'span', 'strong', 'sub', 'summary', 'sup', 's', 'strike', 'table', 'tbody', 'td',
    'tfoot', 'th', 'thead', 'tr', 'u', 'ul',
]);


function isSafeUrl(url) {
    if (!url) return false;
    try {
        const parsed = new URL(url, window.location.origin);
        return ['http:', 'https:', 'mailto:'].includes(parsed.protocol);
    } catch {
        return false;
    }
}

function DetailsBlock({ summary, children }) {
    const [open, setOpen] = useState(true);

    return (
        <div className="not-prose border border-stone-200 rounded-xl overflow-hidden my-4">
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="flex items-center gap-2 w-full px-4 py-3 bg-stone-50 border-b border-stone-100 text-left"
                aria-expanded={open}
                aria-label={`${open ? 'Replier' : 'Développer'} : ${summary}`}
            >
                <ChevronRight size={16} className={`text-stone-400 transition-transform duration-200 ${open ? 'rotate-90' : ''}`} />
                <span className="font-medium text-sm text-stone-700">{summary}</span>
            </button>
            <div className={`px-4 py-3 text-sm text-stone-700 ${open ? '' : 'hidden'}`}>
                {children}
            </div>
        </div>
    );
}

function CalloutBlock({ type, children }) {
    const config = CALLOUT_CONFIG[type] || CALLOUT_CONFIG.info;

    return (
        <div className={`not-prose flex gap-3 p-4 rounded-xl border ${config.classes} my-4`}>
            <div className="flex-shrink-0 pt-0.5">{config.icon}</div>
            <div className="flex-1 min-w-0 text-sm [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                {children}
            </div>
        </div>
    );
}

const COLUMNS_LAYOUT_CLASSES = {
    'equal-2':    'grid-cols-1 md:grid-cols-2',
    'equal-3':    'grid-cols-1 md:grid-cols-3',
    'equal-4':    'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
    'left-wide':  'grid-cols-1 md:grid-cols-[2fr_1fr]',
    'right-wide': 'grid-cols-1 md:grid-cols-[1fr_2fr]',
    'sidebar':    'grid-cols-1 md:grid-cols-[1fr_3fr]',
};

function ColumnsBlock({ layout, children }) {
    const gridClass = COLUMNS_LAYOUT_CLASSES[layout] || COLUMNS_LAYOUT_CLASSES['equal-2'];
    return (
        <div className={`not-prose grid gap-6 my-6 ${gridClass}`}>
            {children}
        </div>
    );
}

function buildReplaceOptions(headingSeen) {
    const seen = headingSeen || {};
    return {
        replace(domNode) {
            if (domNode.type !== 'tag') return;

            if (!ALLOWED_TAGS.has(domNode.name)) return <></>;

            if (domNode.attribs?.style && typeof domNode.attribs.style === 'string') {
                const sanitized = parseInlineStyle(domNode.attribs.style);
                if (sanitized && Object.keys(sanitized).length > 0) {
                    domNode.attribs.style = Object.entries(sanitized)
                        .map(([k, v]) => `${k.replace(/[A-Z]/g, m => '-' + m.toLowerCase())}:${v}`)
                        .join(';');
                } else {
                    delete domNode.attribs.style;
                }
            }

            // Sanitize <a> tags: reject dangerous href values
            if (domNode.name === 'a') {
                const href = domNode.attribs?.href || '';
                if (!isSafeUrl(href)) {
                    return <span>{domToReact(domNode.children, buildReplaceOptions(seen))}</span>;
                }
            }

            // Sanitize <img> tags: reject dangerous src values
            if (domNode.name === 'img') {
                const src = domNode.attribs?.src || '';
                if (!isSafeUrl(src)) return <></>;
            }

            // Task list: <ul data-type="taskList"> with styled checkboxes
            if (domNode.name === 'ul' && domNode.attribs?.['data-type'] === 'taskList') {
                return (
                    <ul className="list-none pl-0 space-y-1 my-2">
                        {(domNode.children || []).filter(c => c.type === 'tag').map((li, i) => {
                            const checked = li.attribs?.['data-checked'] === 'true';
                            return (
                                <li key={i} className="flex items-start gap-2">
                                    <span className={`mt-1 flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center text-white text-3xs ${checked ? 'bg-blue-500 border-blue-500' : 'border-stone-300 bg-white'}`}>
                                        {checked && <Check size={10} />}
                                    </span>
                                    <span className={checked ? 'line-through text-stone-400' : ''}>
                                        {domToReact(li.children, buildReplaceOptions(seen))}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                );
            }

            // Columns layout blocks
            if (domNode.name === 'div' && domNode.attribs?.['data-columns']) {
                const layout = domNode.attribs['data-layout'] || 'equal-2';
                return (
                    <ColumnsBlock layout={layout}>
                        {domToReact(domNode.children, buildReplaceOptions(seen))}
                    </ColumnsBlock>
                );
            }

            // Column cell: restore prose styling inside not-prose grid
            if (domNode.name === 'div' && domNode.attribs?.['data-column'] !== undefined) {
                return (
                    <div className="prose prose-stone max-w-none min-w-0">
                        {domToReact(domNode.children, buildReplaceOptions(seen))}
                    </div>
                );
            }

            // Callout blocks
            if (domNode.name === 'div' && domNode.attribs?.['data-callout-type']) {
                const calloutType = domNode.attribs['data-callout-type'];
                return (
                    <CalloutBlock type={calloutType}>
                        {domToReact(domNode.children, buildReplaceOptions(seen))}
                    </CalloutBlock>
                );
            }

            // Details / accordion blocks
            if (domNode.name === 'details') {
                const summaryChild = domNode.children?.find(c => c.name === 'summary');
                const summaryText = summaryChild ? getTextContent(summaryChild) : 'Détails';
                const contentChildren = domNode.children?.filter(c => c.name !== 'summary') || [];
                return (
                    <DetailsBlock summary={summaryText}>
                        {domToReact(contentChildren, buildReplaceOptions(seen))}
                    </DetailsBlock>
                );
            }

            // Headings: inject id for anchor links (deduplicated)
            if (/^h[1-6]$/.test(domNode.name)) {
                const text = getTextContent(domNode);
                const base = slugify(text);
                seen[base] = (seen[base] || 0) + 1;
                const id = seen[base] > 1 ? `${base}-${seen[base] - 1}` : base;
                const Tag = domNode.name;
                const props = {};
                if (domNode.attribs) {
                    if (domNode.attribs.style) props.style = parseInlineStyle(domNode.attribs.style);
                    if (domNode.attribs.class) props.className = domNode.attribs.class;
                }
                return (
                    <Tag id={id} {...props}>
                        {domToReact(domNode.children, buildReplaceOptions(seen))}
                    </Tag>
                );
            }

            // Code blocks: syntax highlighting + copy button
            if (domNode.name === 'pre') {
                const codeChild = domNode.children?.find(c => c.name === 'code');
                if (!codeChild) return;

                const code = getTextContent(codeChild);
                const langClass = codeChild.attribs?.class || '';
                const langMatch = langClass.match(/language-(\w+)/);
                const language = langMatch ? langMatch[1] : '';

                const highlighted = language ? highlightCode(code, language) : null;
                const isTerminal = /^(bash|shell|sh|zsh)$/.test(language);

                if (isTerminal) {
                    return (
                        <div className="not-prose my-4 rounded-xl overflow-hidden border border-stone-700 bg-stone-950">
                            <div className="flex items-center gap-2 px-4 py-2 bg-stone-800 border-b border-stone-700 text-stone-300 text-xs">
                                <span className="flex gap-1.5">
                                    <span className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
                                    <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
                                    <span className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
                                </span>
                                <span className="ml-2 font-medium tracking-wide">Terminal</span>
                                <span className="ml-auto"><CopyButton text={code} /></span>
                            </div>
                            <pre className="m-0 p-4 overflow-x-auto bg-stone-950 text-green-400">
                                <code className="font-mono text-sm leading-relaxed whitespace-pre">
                                    {highlighted
                                        ? <span dangerouslySetInnerHTML={{ __html: highlighted }} />
                                        : code}
                                </code>
                            </pre>
                        </div>
                    );
                }

                return (
                    <div className="not-prose my-4 rounded-xl overflow-hidden border border-stone-800 bg-stone-900">
                        <div className="doc-code-header">
                            {language && <span className="doc-code-lang">{language}</span>}
                            {!language && <span />}
                            <CopyButton text={code} />
                        </div>
                        {highlighted ? (
                            <pre className="m-0 p-4 overflow-x-auto bg-stone-900 text-stone-200">
                                <code
                                    className="font-mono text-sm leading-relaxed"
                                    dangerouslySetInnerHTML={{ __html: highlighted }}
                                />
                            </pre>
                        ) : (
                            <pre className="m-0 p-4 overflow-x-auto bg-stone-900 text-stone-200">
                                <code className="font-mono text-sm leading-relaxed whitespace-pre">{code}</code>
                            </pre>
                        )}
                    </div>
                );
            }

            // Inline date badges
            if (domNode.name === 'span' && domNode.attribs?.['data-inline-date'] !== undefined) {
                const dateVal = domNode.attribs['data-inline-date'];
                return (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-sm border border-blue-200">
                        <Calendar size={13} className="shrink-0" />
                        <span>{formatInlineDate(dateVal)}</span>
                    </span>
                );
            }

            // Status badges
            if (domNode.name === 'span' && domNode.attribs?.['data-status-badge'] !== undefined) {
                const color = domNode.attribs['data-badge-color'] || 'default';
                const label = getTextContent(domNode);
                const colorClasses = BADGE_COLOR_MAP[color] || BADGE_COLOR_MAP.default;
                return (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${colorClasses}`}>
                        {label}
                    </span>
                );
            }

            // Block all iframes (no embed whitelist active)
            if (domNode.name === 'iframe') return <></>;
            if (domNode.name === 'div' && domNode.attribs?.['data-youtube-video'] !== undefined) return <></>;

        },
    };
}

const DANGEROUS_CSS_PROPS = /^(-moz-binding|behavior|expression)$/i;
const DANGEROUS_CSS_VALUES = /expression\s*\(|url\s*\(/i;

function parseInlineStyle(styleStr) {
    if (!styleStr) return undefined;
    const style = {};
    styleStr.split(';').forEach(rule => {
        const colonIdx = rule.indexOf(':');
        if (colonIdx < 1) return;
        const prop = rule.slice(0, colonIdx).trim();
        const val = rule.slice(colonIdx + 1).trim();
        if (!prop || !val) return;
        if (DANGEROUS_CSS_PROPS.test(prop) || DANGEROUS_CSS_VALUES.test(val)) return;
        const camel = prop.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
        style[camel] = val;
    });
    return style;
}

const INTERNAL_DOC_LINK = /^\/docs\/(page|space)\/\d+/;

export const DocBodyRenderer = ({ props }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);
    const navigate = useNavigate();
    const attributes = useEvaluatedAttributes(props.attributes ?? {});

    const rawHtml = useMemo(() => {
        return evaluateTemplateValue({
            valueToEvaluate: props.content,
            templateContext,
            globalDataContext,
        });
    }, [props.content, templateContext, globalDataContext]);

    const rendered = useMemo(() => {
        if (!rawHtml || typeof rawHtml !== 'string') return null;
        return parse(rawHtml, buildReplaceOptions());
    }, [rawHtml]);

    const handleClick = useCallback((e) => {
        const anchor = e.target.closest('a[href]');
        if (!anchor) return;
        const href = anchor.getAttribute('href');
        if (href && INTERNAL_DOC_LINK.test(href)) {
            e.preventDefault();
            navigate(href);
        }
    }, [navigate]);

    const { class: className, ...restAttrs } = attributes ?? {};

    return (
        <ActionDependant {...props}>
            <div className={className ?? undefined} {...restAttrs} onClick={handleClick}>
                {rendered}
            </div>
        </ActionDependant>
    );
};
